import React, { useState, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import roleSchema from '@galo-monitor/forms/schemas/company/role.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { RoleActions } from '@galo-monitor/stores/role'
import CardAccordion from '@galo-monitor/web-components/CardAccordion'
import Form from '@galo-monitor/web-components/Form'

import ModuleFunctionalities from './ModuleFunctionalities'
import { TextField } from './styles'

const RoleForm = forwardRef((props, formRef) => {
  const { role, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const [functionalityPermissions, setFunctionalityPermissions] = useState({})

  const { data: rolesModules, isValidating: isValidatingRolesModules } = useGaloMonitorApi('/companies/roles/modules/list')
  const { data: currentRoleModules, isValidating: isValidatingCurrentRoleModules } = useGaloMonitorApi(role?.id ? `/companies/roles/${ role.id }` : null)

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)

      const functionalities = map(Object.keys(functionalityPermissions), key => ({
        id: +key,
        reader: functionalityPermissions[key].read,
        created: functionalityPermissions[key].write,
        edited: functionalityPermissions[key].edit,
        deleted: functionalityPermissions[key].delete
      }))

      dispatch(RoleActions.saveRole(
        {
          ...role,
          ...data,
          functionalities
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('role', { howMany: 1 }),
            action: toLower(t(isEmpty(role) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, functionalityPermissions, onError, onSuccess, role, setLoading, snackbar, t]
  )

  const schemaConstructor = useCallback(
    (...params) => roleSchema(...params, { role }),
    [role]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ schemaConstructor }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 12 }>
          <TextField
            name="name"
            label={ `${ t('name of {this}', { this: t('role', { howMany: 1 }), howMany: 1, gender: 'male' }) } Company` }
            defaultValue={ role.name }
            disabled={ loading }
          />
        </Grid>
      </Grid>

      {map(rolesModules, mod => (
        <CardAccordion
          key={ mod.id }
          title={ mod.name }
          defaultOpened
        >
          <ModuleFunctionalities
            isLoading={ loading || isValidatingRolesModules || isValidatingCurrentRoleModules }
            functionalities={ mod.functionalities }
            permissions={
              role.id && !isEmpty(currentRoleModules)
                ? filter(currentRoleModules.functionalities, func => func.moduleName === mod.name)
                : []
            }
            setPermissions={ setFunctionalityPermissions }
          />
        </CardAccordion>
      ))}
    </Form>
  )
})

RoleForm.propTypes = {
  role: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

RoleForm.defaultProps = {
  role: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default RoleForm
